import React from 'react';
import { CerebroDrawer, CerebroHeading, CerebroLink, CerebroSecondaryButton } from '../../shared/components';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Skeleton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDealInvoicesQuery } from '../../shared/hooks';
import { DateUtils, EnumUtils, FileUtils, NumberUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { PlatformRootUrl } from '../../shared/utils/platform';
import { EmptyGuid } from '../../shared/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandHoldingDollar } from '@fortawesome/free-solid-svg-icons';

interface DealInvoiceDrawerProps {
	open?: boolean;
	onClose: () => void;
}

export const DealInvoiceDrawer: React.FC<DealInvoiceDrawerProps> = ({ open, onClose }) => {
	const { guid } = useParams<'guid'>();
	const query = useDealInvoicesQuery(guid, {
		enabled: open,
		retry: false,
	});
	const ref = React.useRef<HTMLDivElement>(null);

	const onManageClicked = (guid: string) => {
		window.open(`${PlatformRootUrl}/Emp/CompanyInvoices/${guid}`, '_blank');
	};

	const onLenderManage = (guid: string) => {
		window.open(`${PlatformRootUrl}/Emp/BankInvoices/${guid}`, '_blank');
	};

	const onUnknownManage = (guid: string) => {
		window.open(`${PlatformRootUrl}/Emp/InvoiceForm/${guid}`, '_blank');
	};

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading>Deal Invoices</CerebroHeading>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 150 }}>#</TableCell>
							<TableCell>Date</TableCell>
							<TableCell>Amount</TableCell>
							<TableCell>Balance</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Last Sent</TableCell>
							<TableCell>Type</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{query.isLoading && (
							<>
								<TableRow>
									<TableCell colSpan={8}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={8}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
							</>
						)}
						{query.isSuccess && (
							<>
								{query.data.map(invoice => {
									return (
										<TableRow key={invoice.guid}>
											<TableCell>
												<CerebroLink
													variant="link"
													target="_blank"
													rel="noreferrer"
													href={CerebroApiEndpoints.Deals.AbsoluteInvoiceDownload(
														invoice.guid
													)}
												>
													{FileUtils.getFileTypeIcon(invoice.invoiceFileName)} {invoice.id}
												</CerebroLink>
											</TableCell>
											<TableCell>
												{invoice.invoiceDate
													? DateUtils.DisplayDateOnly(invoice.invoiceDate?.toString())
													: ''}
											</TableCell>
											<TableCell>
												{NumberUtils.FormatCurrency(invoice.invoiceAmount)}
												{invoice.isCreditMemo && (
													<FontAwesomeIcon
														icon={faHandHoldingDollar}
														size="xs"
														title="Credit Memo"
													/>
												)}
											</TableCell>
											<TableCell>{NumberUtils.FormatCurrency(invoice.balanceDue)}</TableCell>
											<TableCell>
												{EnumUtils.GetInvoiceStatusDescription(invoice.scheduleStatus)}
											</TableCell>
											<TableCell>
												{invoice.lastSentToCompany
													? DateUtils.FormatStringAsShortDate(
															invoice.lastSentToCompany?.toString()
													  )
													: 'Not Sent'}
											</TableCell>
											<TableCell>{invoice.invoiceTypeDisplay}</TableCell>
											<TableCell>
												{invoice.bankGuid ? (
													<CerebroSecondaryButton
														onClick={() => onLenderManage(invoice.bankGuid ?? EmptyGuid)}
													>
														Manage
													</CerebroSecondaryButton>
												) : invoice.companyGuid ? (
													<CerebroSecondaryButton
														onClick={() =>
															onManageClicked(invoice.companyGuid ?? EmptyGuid)
														}
													>
														Manage{' '}
													</CerebroSecondaryButton>
												) : (
													<CerebroSecondaryButton
														onClick={() => onUnknownManage(invoice.guid)}
													>
														Manage
													</CerebroSecondaryButton>
												)}
											</TableCell>
										</TableRow>
									);
								})}
								{query.data.length == 0 && (
									<TableRow>
										<TableCell colSpan={8}>There are no invoices for this deal.</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</CerebroDrawer>
	);
};
